import { useEffect, useState } from "react";
import { useLocation } from "react-router";
import { getSupabaseClient } from "@/components/atomic-crm/providers/supabase/supabase";
import { Layout } from "@/components/supabase/layout";

type AuthVerifyType = "invite" | "recovery";

function isAuthVerifyType(type: string | null): type is AuthVerifyType {
  return type === "invite" || type === "recovery";
}

function getAuthVerifyParams(search: string) {
  const params = new URLSearchParams(search);
  const tokenHash = params.get("token_hash");
  const type = params.get("type");

  if (!tokenHash || !isAuthVerifyType(type)) {
    return null;
  }

  return { tokenHash, type };
}

export const AuthVerifyPage = () => {
  const location = useLocation();
  const [message, setMessage] = useState("Preparing your account...");

  useEffect(() => {
    let cancelled = false;

    async function verify() {
      const authParams = getAuthVerifyParams(location.search);
      if (!authParams) {
        setMessage("This invitation link is invalid or incomplete.");
        return;
      }

      const { data, error } = await getSupabaseClient().auth.verifyOtp({
        token_hash: authParams.tokenHash,
        type: authParams.type,
      });

      if (cancelled) return;

      if (error || !data.session?.access_token || !data.session.refresh_token) {
        setMessage(
          "This invitation link has expired. Ask an administrator to resend it.",
        );
        return;
      }

      const nextParams = new URLSearchParams({
        access_token: data.session.access_token,
        refresh_token: data.session.refresh_token,
        type: authParams.type,
      });

      window.location.replace(`./#/set-password?${nextParams.toString()}`);
    }

    void verify();

    return () => {
      cancelled = true;
    };
  }, [location.search]);

  return (
    <Layout>
      <div className="flex flex-col space-y-2 text-center">
        <h1 className="text-2xl font-semibold tracking-tight">
          Verifying invitation
        </h1>
        <p>{message}</p>
      </div>
    </Layout>
  );
};

AuthVerifyPage.path = "auth-verify";
